import { FC, useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import Spinner from "@/components/graphics/Spinner";
import Button from "@blanket/components/buttons/Button";
import Alert from "@blanket/components/feedback/Alert";
import { SelectFormField } from "@blanket/components/formFields/FormFields";
import FormSection from "@blanket/components/formSections/FormSection";
import type { CompanyRegistrationValues } from "./model/registrationTypes";
import { companyRegistrationInitialValues } from "./model/registrationInitialValues";
import { validateCompanyRegistration } from "./form/registrationYup";
import { buildCompanyRegistrationSubmitPayload } from "./form/registrationFormUtils";
import { EnkeltmandsvirksomhedSection } from "./sections/EnkeltmandsvirksomhedSection";
import { InteressentskabSection } from "./sections/InteressentskabSection";
import { AdvokatselskabSection } from "./sections/AdvokatselskabSection";
import { ChangeInteressentskabSection } from "./sections/ChangeInteressentskabSection";
import { ChangeAdvokatselskabSection } from "./sections/ChangeAdvokatselskabSection";
import { TerminationSection } from "./sections/TerminationSection";
import { sendBlanket } from "@blanket/services/blanketApi";
import { CompanyType, WhatToRegister } from "./model/registrationEnums";
import { useSyncConditionalRegistrationFields } from "./hooks/useSyncConditionalRegistrationFields";

const SyncConditionalFields: FC = () => {
  useSyncConditionalRegistrationFields();
  return null;
};

const DynamicSections: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const { whatToRegister, companyType } = values;

  if (!whatToRegister || !companyType) {
    return null;
  }

  if (whatToRegister === WhatToRegister.Termination) {
    return <TerminationSection />;
  }

  if (whatToRegister === WhatToRegister.Change) {
    if (companyType === CompanyType.Interessentskab) {
      return <ChangeInteressentskabSection />;
    }
    if (companyType === CompanyType.Advokatselskab) {
      return <ChangeAdvokatselskabSection />;
    }
    return null;
  }

  if (whatToRegister === WhatToRegister.Creation) {
    if (companyType === CompanyType.Enkeltmandsvirksomhed) {
      return <EnkeltmandsvirksomhedSection />;
    }
    if (companyType === CompanyType.Interessentskab) {
      return <InteressentskabSection />;
    }
    if (companyType === CompanyType.Advokatselskab) {
      return <AdvokatselskabSection />;
    }
  }

  return null;
};

const CompanyTypeField: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();

  const options = useMemo(() => {
    const shared = [
      { value: CompanyType.Interessentskab, label: "Interessentskab (I/S)" },
      {
        value: CompanyType.Advokatselskab,
        label: "Advokatselskab (A/S, ApS, P/S, herunder holdingselskab)",
      },
    ];

    if (values.whatToRegister === WhatToRegister.Change) {
      return shared;
    }

    return [
      {
        value: CompanyType.Enkeltmandsvirksomhed,
        label: "Enkeltmandsvirksomhed",
      },
      ...shared,
    ];
  }, [values.whatToRegister]);

  return (
    <div className="d-flex flex-column gap-1">
      <SelectFormField
        name="companyType"
        label="Hvilken type af firma drejer det sig om?"
        id="input-company-type"
        isRequired
        placeholder="Vælg type"
        options={options}
      />
      {values.whatToRegister === WhatToRegister.Change ? (
        <p className="small text-muted mb-0">
          Enkeltmandsvirksomhed kan ikke vælges ved ændring. Skal virksomheden
          afvikles, vælg i stedet &quot;Ophør af en advokatvirksomhed&quot;.
        </p>
      ) : null}
      {values.companyType === CompanyType.Advokatselskab &&
      values.whatToRegister === WhatToRegister.Termination ? (
        <div className="small text-muted">
          <p className="mb-2">
            Når et advokatselskab eller et advokatinteressentskab ophører,
            omdannes eller ændrer navn og formål, må selskabet ikke længere have
            betroede midler for klienter. Selskabet skal over for
            Advokatsamfundet erklære, at midlerne er afviklet via en
            sluterklæring, hvilket skal attesteres af revisor. Reglen fremgår af
            klientkontovedtægtens § 15.
          </p>
          <p className="mb-0">
            Læs mere om sluterklæringer på Advokatsamfundets hjemmeside.
          </p>
        </div>
      ) : null}
    </div>
  );
};

const CompanyRegistrationView: FC = () => {
  const initialValues = useMemo(() => companyRegistrationInitialValues(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (values: CompanyRegistrationValues) => {
    const payload = buildCompanyRegistrationSubmitPayload(values);
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);
    try {
      await sendBlanket(payload);
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Ukendt fejl ved indsendelse.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik<CompanyRegistrationValues>
      initialValues={initialValues}
      validate={validateCompanyRegistration}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: handleFormikSubmit, values }) => (
        <Form onSubmit={handleFormikSubmit}>
          <SyncConditionalFields />
          <div className="d-flex flex-column gap-4">
            <FormSection
              id="section-flow"
              number={1}
              title="Hvad vil du registrere?"
              description="Vælg type af registrering og firma for at komme videre."
            >
              <div className="d-flex flex-column gap-4">
                <div className="d-flex flex-column gap-1">
                  <SelectFormField
                    name="whatToRegister"
                    label="Hvad vil du registrere?"
                    id="input-what-to-register"
                    isRequired
                    placeholder="Vælg type"
                    options={[
                      {
                        value: "creation",
                        label: "Oprettelse af ny advokatvirksomhed",
                      },
                      {
                        value: "change",
                        label:
                          "Ændring i ejerkredsen ved udtræden eller indtræden",
                      },
                      {
                        value: "termination",
                        label: "Ophør af en advokatvirksomhed",
                      },
                    ]}
                  />
                  {values.whatToRegister === WhatToRegister.Termination && (
                    <div className="small text-muted">
                      <p className="mb-2">
                        En advokatvirksomhed vil blive betragtet som ”ophørt”,
                        når (1) virksomheden ophører fuldstændigt, samt når (2)
                        virksomhedens navn og formål ændres.
                      </p>
                      <p className="mb-2">
                        For så vidt angår (2) skyldes dette, at et
                        advokatselskab skal benytte den lovpligtige betegnelse
                        afhængigt af selskabsformen (fx X Advokater) og have til
                        formål at &quot;udøve advokatvirksomhed&quot; i
                        CVR-registret. Ændres navn og formål, så de ikke
                        opfylder disse krav, anses advokatvirksomheden for
                        ophørt.
                      </p>
                      <p className="mb-0">
                        Uanset om virksomheden ophører (1) eller skifter navn og
                        formål (2), skal det registres i denne blanket.
                      </p>
                    </div>
                  )}
                  {values.whatToRegister === WhatToRegister.Creation && (
                    <p className="small text-muted mb-0">
                      Vær opmærksom på, at det ved etablering af
                      advokatselskaber er den øverste ledelse, der skal foretage
                      registreringerne via denne blanket, jf. bekendtgørelse om
                      advokatselskaber § 6, stk. 1.
                    </p>
                  )}
                  {values.whatToRegister === WhatToRegister.Change && (
                    <p className="small text-muted mb-0">
                      Vær opmærksom på, at det ved registrering af ændringer er
                      den øverste ledelse, der skal foretage registreringerne
                      via denne blanket, jf. bekendtgørelse om advokatselskaber
                      § 6, stk. 1.
                    </p>
                  )}
                </div>
                <CompanyTypeField />
              </div>
            </FormSection>

            <DynamicSections />

            {submitError || submitSuccess ? (
              <div className="d-flex flex-column gap-3">
                {submitError ? (
                  <Alert
                    type="danger"
                    message={submitError}
                    onClose={() => setSubmitError(null)}
                  />
                ) : null}
                {submitSuccess ? (
                  <Alert
                    type="success"
                    message="Blanketten er indsendt."
                    onClose={() => setSubmitSuccess(false)}
                  />
                ) : null}
              </div>
            ) : null}

            <div className="blanket-submit-bar">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                fullWidth={false}
                className="px-4"
              >
                {isSubmitting ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <Spinner inline size={16} label="" />
                    Sender...
                  </span>
                ) : (
                  "Send blanket"
                )}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CompanyRegistrationView;
